import { APPLICATION_URL } from '@/app-config';
import { IMondayContext } from '@/types/response/monday/context';

export const SIGN_REQUEST_ERROR_TYPE = Object.freeze({
  MISSING_RECIPIENT: 'MISSING_RECIPIENT',
  USING_PREMIUM_FIELDS: 'USING_PREMIUM_FIELDS',
  USING_CK_SUB_ITEMS: 'USING_CK_SUB_ITEMS',
  INVALID_ACCESS_TOKEN: 'INVALID_ACCESS_TOKEN',
  // MISSING_PEOPLE_COLUMN: 'MISSING_PEOPLE_COLUMN',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  SENDING_LIMIT_EXCEEDED: 'SENDING_LIMIT_EXCEEDED',
  MISSING_SENDER_EMAIL: 'MISSING_SENDER_EMAIL',
  MISSING_SIGNER_EMAIL: 'MISSING_SIGNER_EMAIL',
  MISSING_SIGNATURE_FIELDS: 'MISSING_SIGNATURE_FIELDS',
  LIMIT_REACHED: 'LIMIT_REACHED',
});

export type PremiumFieldKey = keyof typeof PREMIUM_FIELDS;

export const PREMIUM_FIELDS = {
      'text-box': 'Text Input Fields',
      'checkbox': 'Checkbox Fields',
      'sd-textarea': 'Textarea Fields',
      'two_factor_enforced': 'OTP Password Protection',
      'radio-button': 'Radio Button Fields',
      'sd-dropdown': 'Dropdown Fields',
      'date-picker': 'Date Picker Fields',
      'signature': 'Signature Fields',
      'initial': 'Initial Fields',
      'sd-payment': 'Payment Fields',
      'signing_order_required': 'Signing Order',
      'multiple_templates': 'Multiple Templates',
    } as const;

export const CURRENCY = 'USD';

export const ONBOARDING_FLOW = {
  NEW: 'new',
  OLD: 'old',
};

export const APPLICATION_STATUS = {
  INITIALIZED: 'initialized',
  INFO_PAGE_DISPLAYED: 'info-page-displayed',
};

export const ENVELOPE_STATUS = {
  INITIALIZED: 'initialized',
  SETTINGS_CONFIGURED: 'settings-configured',
};

export const LINKS = {
  CHAT_WITH_US:
    'https://guest.getresponse.chat/?c=9de079a759ca5a1629330d1f86505a086e89008391e5b3664dce375d67',
  NEED_HELP: 'https://getsign.io/resource/',
  PRICING: 'https://getsign.io/getsign-pricing',
  DIGITAL_SIGNATURE_RESOURCE: 'https://getsign.io/digital-signature-on-getsign',
  INFO_ESIGNATURE: 'https://getsign.io/feature/esignature/',
  INFO_GENERATE: 'https://getsign.io/feature/document-generation/',
  INFO_SEND: 'https://getsign.io/capabilities/getsign-send/',
  INFO_TRACK: 'https://getsign.io/capabilities/getsign-track/',
};

export const NEW_ONBOARDING_WORKFLOW = 'new-onboarding-workflow';

export const APP_ROUTES = {
  SETUP: '/new-onboarding/setup',
  SETTINGS: '/new-onboarding/settings',
  INFO: '/new-onboarding/info',
};

export const APPLICATION_ROUTES = {
  PDF_SIGN: `${APPLICATION_URL}/pdf-sign/:itemId/:fileStatus`,
  SHORT_SIGN_LINK: `${APPLICATION_URL}/sign/:shortId`,
  TEMPLATE_GALLERY_EDITOR: ({
    templateId,
    fileId,
    sessionToken,
    context,
    isPreview,
  }: {
    templateId: string;
    fileId: string;
    sessionToken: string;
    context: IMondayContext;
    isPreview?: boolean;
  }) => {
    const itemId = context?.itemId;
    const currentVersion = context?.appVersion?.versionData
      ? JSON.stringify(context.appVersion.versionData)
      : '';

    const pathSegments = [
      APPLICATION_URL,
      'template',
      'v3',
      templateId,
      fileId,
      itemId,
    ].filter(Boolean);

    const url = new URL(pathSegments.join('/'));

    if (sessionToken) url.searchParams.set('sessionToken', sessionToken);
    if (isPreview) url.searchParams.set('isPreview', isPreview.toString());

    if (currentVersion) url.searchParams.set('version', currentVersion);

    return url.toString();
  },
  PDF_EDITOR: ({
    envelopeId,
    itemId,
    fileId,
    sessionToken,
    shouldEditTemplate,
  }: {
    envelopeId: string;
    itemId: number;
    fileId: string;
    sessionToken?: string;
    shouldEditTemplate?: boolean;
  }) => {
    const pathSegments = [
      APPLICATION_URL,
      'pdf-editor',
      envelopeId,
      itemId,
      fileId,
    ].filter(Boolean);
    const url = new URL(pathSegments.join('/'));

    if (sessionToken) url.searchParams.set('sessionToken', sessionToken);
    if (shouldEditTemplate) url.searchParams.set('tmpl', 'true');
    url.searchParams.set('isV3', 'true');

    return url.toString();
  },
  FORM_EDITOR: ({
    envelopeId,
    sessionToken,
  }: {
    envelopeId: string;
    sessionToken?: string;
  }) => {
    const pathSegments = [APPLICATION_URL, 'form-editor', envelopeId].filter(
      Boolean,
    );
    const url = new URL(pathSegments.join('/'));
    if (sessionToken) url.searchParams.set('sessionToken', sessionToken);
    return url.toString();
  },
  PDF_PREVIEW: ({
    envelopeId,
    itemId,
    fileId,
    sessionToken,
  }: {
    envelopeId: string;
    itemId: number;
    fileId: string;
    sessionToken?: string;
  }) => {
    const pathSegments = [
      APPLICATION_URL,
      'pdf-preview',
      envelopeId,
      itemId,
      fileId,
    ].filter(Boolean);

    const url = new URL(pathSegments.join('/'));

    if (sessionToken) url.searchParams.set('sessionToken', sessionToken);
    url.searchParams.set('isPreview', 'true');

    return url.toString();
  },
  WORD_EDITOR: ({
    envelopeId,
    itemId,
    fileId,
    fromTemplateGallery,
    templateId,
  }: {
    envelopeId: string;
    itemId: number;
    fileId: string;
    fromTemplateGallery: boolean;
    // Required when fromTemplateGallery is true (see firstSegment note below).
    templateId?: string;
  }) => {
    // The word editor reuses this first path segment as the id it redirects
    // back with: the templateId for the template-gallery flow
    // (/template/v3/:templateId/...) or the envelopeId for the pdf flow
    // (/pdf-editor/:envelopeId/...). It is NOT always an envelopeId despite the
    // route param name — so the template-gallery flow must supply templateId.
    const firstSegment = fromTemplateGallery ? templateId : envelopeId;
    const pathSegments = [
      APPLICATION_URL,
      'word-editor',
      firstSegment,
      itemId,
      fileId,
    ].filter(Boolean);
    const url = new URL(pathSegments.join('/'));

    if (fromTemplateGallery) {
      url.searchParams.set('fromTemplateGallery', 'true');
      // Gallery edits are template-level: mark the session ('tmpl') so the
      // editor shows the "editing at the template gallery level" scope banner
      // rather than the misleading "item level" message.
      url.searchParams.set('tmpl', 'true');
    }

    return url.toString();
  },
};

export const DEFAULT_EMAIL_MESSAGE = 'Your signature is requested!';

export const LANGUAGE_OPTIONS = [
  { label: 'English (Default)', value: 'en' },
  { label: 'French', value: 'fr' },
  { label: 'Hebrew', value: 'he' },
  { label: 'Portuguese', value: 'pt' },
  { label: 'Spanish', value: 'es' },
  { label: 'Japanese', value: 'ja' },
];

export const THUMBNAILS_BASE_URL = 'https://dxjymc4ttjr54.cloudfront.net';

export const PERMISSION_MESSAGE = {
  EDIT_DOCUMENT_NOT_ALLOWED: 'Only board owners can edit documents.',
  GENERATE_DOCUMENT_NOT_ALLOWED: 'Only board owners can generate documents.',
  EDIT_SETTINGS_NOT_ALLOWED: 'Only board owners can update the settings.',
  ADD_TEMPLATE_NOT_ALLOWED: 'Only board owners can add templates.',
  UPLOAD_NEW_DOCUMENT_NOT_ALLOWED:
    'Only board owners can upload new documents.',
  ONLY_FIRST_DOCUMENT_EDITABLE:
    'You can only edit the first document in this Plan.',
};

export const RESERVED_COLUMN_LABEL = {
  GENERATING_DOCUMENT: 'Generating...',
  PDF_GENERATED: 'PDF Generated',
  DOCX_GENERATED: 'DOCx Generated',
  DOC_GENERATED: 'DOC Generated',
  SENT: 'Sent',
  LINK_GENERATED: 'GetSign Link Generated',
};

export const EDIT_DOCUMENT_MESSAGE = {
  EDITING_SINGLE_DOCUMENT: `You're editing at the item level. Changes made here will apply only to this specific item.`,
  EDITING_TEMPLATE: `You're editing at the template gallery level. Any changes made here will be applied to all items using this template.`,
};

export const ROOT_USER_IDS = [
  68545622, // support@jetpackapps.co
];

export const FILE_TYPE = {
  PDF: 'pdf',
  DOC: 'doc',
  DOCX: 'docx',
};

export const LEGACY_ACCOUNT_IDS = [
  5218748, // - operations715
  31338334, // - soda seller
];


export const LOCAL_STORAGE_KEYS = {
  IS_FLOATING_VIDEO_VISIBLE: 'isFloatingVideoVisible',
};

export const CLARITY_TAG_NAME = {
  PAGE_NAME: 'app',
  PLATFORM: 'platform',
};

export const CLARITY_TAG_VALUE = {
  PAGE_NAME: {
    TEMPLATE_GALLERY: 'template-gallery',
    ITEM_VIEW_SETTINGS: 'item-view-settings',
  },
};
